import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { KeyValue } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppPath } from '@app/app-path';
import { AppLocale } from '@app/core/models/app-locale';
import { OrderAdDetail } from '@app/core/models/order-ad-detail.model';
import { Order } from '@app/core/models/order.model';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppLocaleService } from '@app/core/services/app-locale.service';
import { OrdersService } from '@app/core/services/orders.service';
import { DialogComponent } from '@app/shared/components/dialog/dialog.component';
import { CassCurrencyPipe } from '@app/shared/directives/cass-currency.pipe';
import { DialogAction } from '@app/shared/models/dialog-action';
import { DialogData } from '@app/shared/models/dialog-data';
import { OrderAdDetailStatus } from '@core/enums/order-ad-detail-status';
import { CassError } from '@core/models/cass-error';
import { Package } from '@core/models/package.model';
import { Product } from '@core/models/product.model';
import { SpaceAvailability } from '@core/models/space-availability';
import { SpaceAvailabilityRequest } from '@core/models/space-availability-request';
import { SpaceAvailabilityRequestRecord } from '@core/models/space-availability-request-record';
import { Logger } from '@core/services/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { OrderEditRestriction } from '@order/models/order-edit-restriction';
import { OrderLineNavigationState } from '@order/models/order-line-navigation-state';
import { OrderNavigationState } from '@order/models/order-navigation-state';
import { OrderOwner } from '@order/models/order-owner';
import { PlacementSize } from '@order/models/placement-size';
import { CalculatePriceRequestService } from '@order/services/calculate-price-request.service';
import { InfoPopupService } from '@order/services/info-popup.service';
import { OrderHelperService } from '@order/services/order-helper.service';
import { ProductPlacementSizeComboService } from '@order/services/product-placement-size-combo.service';
import { StaticDataCacheService } from '@order/services/static-data-cache.service';
import { StrictPackageGroupsService } from '@order/services/strict-package-groups.service';
import { IssueViewModel } from '@order/viewModels/issue.viewModel';
import { PlacementViewModel } from '@order/viewModels/placement.viewModel';
import { ProductViewModel } from '@order/viewModels/product.viewModel';
import { PubIssuesRecordViewModel } from '@order/viewModels/pub-issues-record.viewModel';
import { SizeViewModel } from '@order/viewModels/size.viewModel';
import { IndeterminateHorizontalProgressIndicatorComponent } from '@shared/components/indeterminate-horizontal-progress-indicator/indeterminate-horizontal-progress-indicator.component';
import { DateTimeService } from '@shared/services/date-time.service';
import { StatusService } from '@shared/services/status.service';
import { UserAlertService } from '@shared/services/user-alert.service';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, firstValueFrom, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { SubSink } from 'subsink';


type ProductFilter = {
	selected: string,
	options: KeyValue<number, string>[], // key defines the sort order, value is the selection option
	searchQuery: string,
	controlType: number,
	categorySortNumber: number,
	categoryLabel: string
}

@Component({
	selector: 'app-order-line',
	templateUrl: './order-line.component.html',
	styleUrls: ['./order-line.component.css']
})
export class OrderLineComponent implements OnInit, OnDestroy {

	private readonly COMPONENT_NAME = "OrderLineComponent";

	private readonly PLACEMENT_REQUEST_DEBOUNCE_TIME_MSEC = 750;

	matrixPubsAndIssues: PubIssuesRecordViewModel[] = []; // Package Id is used as the publication id

	private readonly calendarPeriod = 366;
	private readonly SPACE_AVAILABLITY__MAX_PRODUCTS_PER_REQUEST = 5;
	private readonly SPACE_AVAILABLITY__MAX_DATES_PER_PRODUCT = 31;

	selectionStep: number = 0;
	spaceAvailabilityRequestProgressValue: number = 0;
	isMatrixVisible: boolean = false;

	constructor (
		public localeService: AppLocaleService,
		private _appConfigService: AppConfigService,
		private _alertService: UserAlertService,
		private _calculatePriceRequestService: CalculatePriceRequestService,
		private _dateTimeService: DateTimeService,
		private _logger: Logger,
		private _orderHelperService: OrderHelperService,
		private _orderService: OrdersService,
		private _overlay: Overlay,
		private _popoverService: InfoPopupService,
		private _productPlacementSizeComboService: ProductPlacementSizeComboService,
		private _router: Router,
		private _staticDataCacheService: StaticDataCacheService,
		private _statusService: StatusService,
		private _translate: TranslateService,
		private _strictPackageGroupsService: StrictPackageGroupsService,
		private _dialog: MatDialog
	) {
		this.currencyPipe = new CassCurrencyPipe(localeService, this._appConfigService);

		const nav = this._router.getCurrentNavigation();
		if (nav && nav.extras && nav.extras.state) {
			const state = nav.extras.state as OrderLineNavigationState;

			this._inputState = state;

			this.originalOrder = state.order;
			this.restriction = state.restriction || this.restriction;
			this.originalOrderAdDetail = state.orderAdDetail;
			this._orderHasUnsavedChanges = state.orderHasUnsavedChanges || false;
			this._orderOwner = state.orderOwner;

			if (this.originalOrder) {
				this.isDirectOrder = this._orderHelperService.getIsDirectOrder(this.originalOrder);
				if (this.originalOrder.orderAdDetails) {
					this._initialOrderLines = [...this.originalOrder.orderAdDetails];
				}
			}
		}
	}

	readonly originalOrder?: Order; // the order received from the Order Component. Used when the user cancels the operation. Any changes are applied to this.updatedOrder.
	readonly originalOrderAdDetail?: OrderAdDetail;  // the Ad Detail is set only when editing existing order line. Any changes are applied to this.updatedOrderAdDetail. It is updated when user clicks Apply.
	readonly isDirectOrder: boolean = false;

	updatedOrder?: Order;
	updatedOrderAdDetail?: OrderAdDetail;

	private readonly _initialOrderLines: OrderAdDetail[] = [];

	private _inputState: OrderLineNavigationState | undefined;

	isNewLine: boolean = false;
	private _orderHasUnsavedChanges: boolean = false;
	currencyPipe: CassCurrencyPipe;

	priceGross = 0;
	priceNet = 0;
	priceNetExComm = 0;
	discount = 0;
	commission = 0;

	restriction: OrderEditRestriction = OrderEditRestriction.createDefault();

	placementRequest = new FormControl(""); // User's placement request note

	productsVMs: ProductViewModel[] = []; // CASS packages are UI products
	placementsVMs: PlacementViewModel[] = [];
	sizesVMs: SizeViewModel[] = [];

	selectedPackagesIds = new Set<string>();
	selectedPackagesLabels: string | undefined = undefined;

	selectedPlacementLabel: string | undefined = undefined;
	private _selectedPlacementId: string | undefined = undefined;
	showPlacementApprovalLegend: boolean = false;

	selectedSizeLabel: string | undefined = undefined;

	insertionCounter: number = 0;
	dateCounter: number = 0;

	private _orderOwner: OrderOwner | undefined;

	// Space availability cache: Map<date-productCode-placementCode-sizeLabel, boolean>
	private _spaceAvailabilityCache = new Map<string, boolean>();
	private _spaceAvailabilitySub: Subscription | undefined;

	// This subject is used to cancel the current batch of space availability requests, using next() and optional complete() methods
	private _spaceAvailabilityCancellationSubject$: Subject<void> | undefined;

	// This subject pauses the current space availability requests batch by setting it to true to pause and to false to resume; Calc pauses the batch when active.
	private pauseSpaceAvailabilityBatch$ = new BehaviorSubject<boolean>(false);

	private readonly _callingOut$ = new BehaviorSubject<boolean>(false);
	private _fullscreenProgressOverlayRef?: OverlayRef;
	private _subs = new SubSink();

	canApply = false;
	private _calc$ = new BehaviorSubject<number>(Date.now()); // Observer for deferred calc

	private _lastChangeTimeStamp: number | undefined;

	localeId: string = "en";

	ngOnInit(): void {
		this._fullscreenProgressOverlayRef = this._overlay.create({
			positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
			hasBackdrop: true,
			disposeOnNavigation: true
		});

		this._subs.add(
			this._callingOut$.pipe(distinctUntilChanged()).subscribe(show => {
				if (show) {
					const componentRef = this._fullscreenProgressOverlayRef?.attach(new ComponentPortal(IndeterminateHorizontalProgressIndicatorComponent));
					if (componentRef) {
						this._translate.get("APP_NOTIFICATIONS.PLEASE_WAIT_MESSAGE").subscribe((msg) => componentRef.instance.displayString = msg);
					}
				}
				else {
					this._fullscreenProgressOverlayRef?.detach();
				}
			}));

		if (!this.originalOrder) {
			this._logger.error(this.COMPONENT_NAME, "OrderLineComponent: Init: order expected but not received");
		}
		else {
			if (this.originalOrderAdDetail) {
				this.updatedOrderAdDetail = Object.assign({}, this.originalOrderAdDetail);
			}
			else {
				this.isNewLine = true;
			}
		}

		this._subs.add(
			this.localeService.currentLocale$.subscribe((locale: AppLocale | undefined) => {
				if (locale && locale.localeId && locale.localeId !== this.localeId) {
					this.localeId = locale.localeId;

					// re-sort the products and placements using new locale
					this.productsVMs = this.sortProductsVMs(this.productsVMs);
					this.placementsVMs = this.sortPlacementsVMs(this.placementsVMs);
				}
			})
		);

		this._callingOut$.next(true);
		const staticDataInit$ = this._staticDataCacheService.init();
		const comboServiceInit$ = this._productPlacementSizeComboService.init();

		this._subs.add(
			// read only the initial value of this.localeService.currentLocale$; its change will be handled separately
			combineLatest([staticDataInit$, comboServiceInit$]).subscribe({
				next: ([_a, _b]) => {
					this._callingOut$.next(false);
					const productsVMs = this._staticDataCacheService.packages.map((p: Package) => new ProductViewModel(p.id, p.label));
					this.productsVMs = this.sortProductsVMs(productsVMs);

					this.productFilters = this.createProductFilters(this._staticDataCacheService.products);

					this.placementsVMs = [];
					let placements = this._staticDataCacheService.placements;
					const placementSizesRelations = this._productPlacementSizeComboService.placementSizesRelations;
					const placementsVMs: PlacementViewModel[] = [];
					for (let placement of placements) {
						const placementSizesRelation = placementSizesRelations.get(placement.id);
						// list only those placements which have placement-sizes-relation defined
						if (placement && placementSizesRelation) {
							const placementVM = new PlacementViewModel(placement, placementSizesRelation);
							placementsVMs.push(placementVM);
						}
					}

					this.placementsVMs = this.sortPlacementsVMs(placementsVMs);

					this.updatePlacementApprovalLegendVisibility();

					const sizesVMs = this._productPlacementSizeComboService.allPlacementSizes.map((size: PlacementSize) => new SizeViewModel(size));

					this.sizesVMs = sizesVMs.sort((a, b) => {
						let diff = b.depthRatio * b.widthRatio - a.depthRatio * a.widthRatio;
						if (diff === 0) {
							// If depth * width is the same, sort by name (case-insensitive) using the selected locale
							return a.label.toUpperCase().localeCompare(b.label.toUpperCase(), this.localeId);
						}
						else {
							return diff;
						}
					});

					if (!this.isNewLine && this.updatedOrderAdDetail) {
						this.displayOrderLine(this.updatedOrderAdDetail);
					}
				},
				error: (error) => {
					//TODO - show anything to the user?
					this._callingOut$.next(false);
					this._logger.error(this.COMPONENT_NAME, "Init of static data service/combo service failed", error);
				}
			}));

		this._subs.add(
			this.placementRequest.valueChanges
				.pipe(
					debounceTime(this.PLACEMENT_REQUEST_DEBOUNCE_TIME_MSEC),
					distinctUntilChanged()
				)
				.subscribe(_ => {
					this.notifyPropertyChanged();
				})
		);


		this._subs.add(
			this._calc$
				.pipe(debounceTime(1500))
				.subscribe((timeStamp) => this.deferredCalc(timeStamp))
		);
	}

	ngOnDestroy(): void {
		this.cancelSpaceAvailabilityBatch();
		this._subs.unsubscribe();

		if (this._fullscreenProgressOverlayRef && this._fullscreenProgressOverlayRef.hasAttached()) {
			this._fullscreenProgressOverlayRef.detach();
		}
	}

	private displayOrderLine(orderAdDetail: OrderAdDetail): void {
		this.selectOrderLineProduct(orderAdDetail.packageId);
		this.selectOrderLinePlacement(orderAdDetail.placementId);
		this.selectOrderLineSize(orderAdDetail);

		if (orderAdDetail.placementRequest) {
			this.placementRequest.setValue(orderAdDetail.placementRequest);
		}

		this.updateProductsValidity();
		this.updatePlacementsValidity();
		this.updateSizesValidity();
		this.updateMatrixPubs();
		this.updateSpaceAvailability();
		this.toggleOrderLineIssue(orderAdDetail.issueDate, orderAdDetail.packageId, true);
		this.updateComponentCosting([orderAdDetail]);
	}

	private selectOrderLineProduct(packageCode: string): void {
		if (packageCode) {
			const selectedProductVM = this.productsVMs.find(productVM => productVM.id === packageCode);
			if (selectedProductVM) {
				//this.toggleProduct(selectedProductVM);
				this.toggleProduct(selectedProductVM);
			}
		}
	}

	private selectOrderLinePlacement(placementCode: string): void {
		if (placementCode) {
			const selectedPlacementVM = this.placementsVMs.find(value => value.id === placementCode)
			if (selectedPlacementVM) {
				this.togglePlacement(selectedPlacementVM);
			}
		}
	}

	private selectOrderLineSize(orderAdDetail: OrderAdDetail): void {

		if (!orderAdDetail.packageId || !orderAdDetail.placementId) {
			return;
		}

		// TODO could refactor to use _productPlacementSizeComboService.getFixedSizeByDimensions
		let targetPlacementSize: PlacementSize | undefined = undefined;
		const placementSizes = this._productPlacementSizeComboService.getPlacementSizesPerProductAndPlacement(orderAdDetail.packageId, orderAdDetail.placementId);
		if (placementSizes) {
			const moduleCode = orderAdDetail.moduleCode;
			if (moduleCode) {
				// find the size by module code
				const uppercaseModuleCode = moduleCode.toUpperCase();
				for (let placementSize of placementSizes) {
					if (placementSize.moduleCode && placementSize.moduleCode.toUpperCase() === uppercaseModuleCode) {
						targetPlacementSize = placementSize;
						break;
					}
				}
			}
			else {
				// find the size by columns x depth (height)
				for (let placementSize of placementSizes) {
					if (placementSize.columns === orderAdDetail.columns && placementSize.height === orderAdDetail.depth) {
						targetPlacementSize = placementSize;
						break;
					}
				}
			}
		}

		if (targetPlacementSize) {
			// the PlacementSize is found; now select the View Model corresponding to this size
			const uppercaseSizeLabel = targetPlacementSize.label?.toUpperCase();
			const sizeVM = this.sizesVMs.find(vm => vm.label.toUpperCase() === uppercaseSizeLabel);
			if (sizeVM) {
				this.toggleSize(sizeVM);
			}
		}
	}

	uiToggleDate(arg: { dateString: string, pubId: string | undefined }): void {
		if (arg && arg.dateString) {
			this.toggleOrderLineIssue(arg.dateString, arg.pubId);
			this.notifyPropertyChanged();
		}
	}

	private toggleOrderLineIssue(dateString: string, pubId: string | undefined, forceSelectIfNotAvailable: boolean = false): void {
		// note: when editing existing order line, only one date can be selected

		if (!dateString) {
			this._logger.warning(this.COMPONENT_NAME, "A bug in the OrderLineComponent/MultiPubIssuesMatrixComponent: toggleOrderLineIssue() is called with empty dateString");
			return;
		}

		if (this.isNewLine) {
			if (pubId) {
				// pub Id is provided - process click only for this pub Id
				const pubIssuesVM = this.matrixPubsAndIssues.find((pubIssues: PubIssuesRecordViewModel) => pubIssues.pubId === pubId);

				if (!pubIssuesVM) {
					this._logger.error(this.COMPONENT_NAME, `A bug in the OrderLineComponent/MultiPubIssuesMatrixComponent: toggleOrderLineIssue() is called with invalid pubId="${pubId}", it cannot be found in the matrix`);
					return;
				}

				const issueVM = pubIssuesVM.issues.find((issue: IssueViewModel) => issue.dateCode === dateString);
				if (!issueVM) {
					this._logger.error(this.COMPONENT_NAME, `A bug in the OrderLineComponent/MultiPubIssuesMatrixComponent: toggleOrderLineIssue() is called with pubId="${pubId}" and invalid date="${dateString}", it cannot be found in the matrix`);
					return;
				}

				this.toggleIssueVM(issueVM);
			}
			else {
				// pub Id is not provided - process click for all pubs in the matrix
				if (this.matrixPubsAndIssues.some((val: PubIssuesRecordViewModel) => this.isSameOrNextAvailableIssueSelected(val, dateString))) {
					// deselect same or next available dates for all pubs
					this.matrixPubsAndIssues.forEach((val: PubIssuesRecordViewModel) => this.deselectSameOrNextAvailableIssue(val, dateString));
				}
				else {
					// select same or next available dates for all pubs
					this.matrixPubsAndIssues.forEach((val: PubIssuesRecordViewModel) => this.selectSameOrNextAvailableIssue(val, dateString));
				}
			}
		}
		else {
			if (this.matrixPubsAndIssues.length === 0) {
				this._logger.error(this.COMPONENT_NAME, "A bug in the OrderLineComponent/MultiPubIssuesMatrixComponent: toggleOrderLineIssue() is called for an existing order line when the matrix is empty");
				return;
			}

			if (this.matrixPubsAndIssues.length > 1) {
				this._logger.warning(this.COMPONENT_NAME, `A bug in the OrderLineComponent/MultiPubIssuesMatrixComponent: toggleOrderLineIssue() is called for an existing order line when the matrix contains ${this.matrixPubsAndIssues.length} items. It must have one item.`);
				return;
			}

			const pubAndIssues = this.matrixPubsAndIssues[0];

			const issue = this.getIssue(pubAndIssues, dateString);
			if (!issue) {
				this._logger.warning(this.COMPONENT_NAME, `A bug in the OrderLineComponent/MultiPubIssuesMatrixComponent: toggleOrderLineIssue() is called with a date="${dateString}" which cannot be found for the packageId="${pubAndIssues.pubId}".`);
				return;
			}

			if (issue.isSelected) {
				issue.isSelected = false;
			}
			else if (issue.nullableIsValid === true) {
				pubAndIssues.issues.forEach((iss: IssueViewModel) => iss.isSelected = false);
				issue.isSelected = true;
			}
			else if (pubId && forceSelectIfNotAvailable) {
				issue.isSelected = true;
			}
		}

		this.updateSelectionCounters();
	}

	private toggleIssueVM(issueVM: IssueViewModel): void {
		if (issueVM.isSelected) {
			issueVM.isSelected = false;
		}
		else if (issueVM.nullableIsValid === true) {
			// select the issue only if valid/available
			issueVM.isSelected = true;
		}
	}

	private isSameOrNextAvailableIssueSelected(pubIssuesVM: PubIssuesRecordViewModel, dateString: string): boolean {

		const issueVM = this.getIssue(pubIssuesVM, dateString);

		if (!issueVM) {
			this._logger.error(this.COMPONENT_NAME, `A bug in the OrderLineComponent/MultiPubIssuesMatrixComponent: isSameOrNextAvailableIssueSelected() is called with a dateString=${dateString} which cannot be found for pubId="${pubIssuesVM.pubId}"`);
			return false;
		}

		if (issueVM.isSelected) {
			return true;
		}

		// find the next available date only if the given date is not available
		if (issueVM.nullableIsValid === false) {
			const nextValidIssue = this.getNextValidIssue(pubIssuesVM, dateString);
			if (nextValidIssue && nextValidIssue.isSelected) {
				return true;
			}
		}

		return false;
	}

	private deselectSameOrNextAvailableIssue(pubIssuesVM: PubIssuesRecordViewModel, dateString: string): void {
		const issueVM = this.getIssue(pubIssuesVM, dateString);
		if (!issueVM) {
			this._logger.error(this.COMPONENT_NAME, `A bug in the OrderLineComponent/MultiPubIssuesMatrixComponent: deselectSameOrNextAvailableIssue() is called with a dateString=${dateString} which cannot be found for pubId="${pubIssuesVM.pubId}"`);
			return;
		}

		if (issueVM.isSelected) {
			issueVM.isSelected = false;
		}
		else if (issueVM.nullableIsValid === false) {
			const nextValidIssue = this.getNextValidIssue(pubIssuesVM, dateString);
			if (nextValidIssue && nextValidIssue.isSelected) {
				nextValidIssue.isSelected = false;
			}
		}
	}

	private selectSameOrNextAvailableIssue(pubIssuesVM: PubIssuesRecordViewModel, dateString: string): void {
		const issueVM = this.getIssue(pubIssuesVM, dateString);
		if (!issueVM) {
			this._logger.error(this.COMPONENT_NAME, `A bug in the OrderLineComponent/MultiPubIssuesMatrixComponent: selectSameOrNextAvailableIssue() is called with a dateString=${dateString} which cannot be found for pubId="${pubIssuesVM.pubId}"`);
			return;
		}

		if (issueVM.nullableIsValid === true) {
			if (!issueVM.isSelected) {
				issueVM.isSelected = true;
			}
		}
		else {
			const nextValidIssue = this.getNextValidIssue(pubIssuesVM, dateString);
			if (nextValidIssue && !nextValidIssue.isSelected) {
				nextValidIssue.isSelected = true;
			}
		}
	}

	private getIssue(pubIssuesVM: PubIssuesRecordViewModel, dateString: string): IssueViewModel | undefined {
		return pubIssuesVM.issues.find((issue: IssueViewModel) => issue.dateCode === dateString);
	}

	private getNextValidIssue(pubIssuesVM: PubIssuesRecordViewModel, dateString: string): IssueViewModel | undefined {
		return pubIssuesVM.issues.find((issue: IssueViewModel) => issue.dateCode > dateString && issue.nullableIsValid === true);
	}

	uiToggleProduct(productVM: ProductViewModel): void {
		this.cancelSpaceAvailabilityBatch();

		this.toggleProduct(productVM);
		this.toggleStrictPackageGroup(productVM);
		this.updatePlacementsValidity();
		this.updateSizesValidity();
		this.updateMatrixPubs();
		this.updateSpaceAvailability();
		this.notifyPropertyChanged();
	}

	private toggleProduct(productVM: ProductViewModel): void {
		// multi-select when creating new order lines; single-select when editing existing order line

		productVM.isSelected = !productVM.isSelected;
		if (productVM.isSelected) {
			if (!this.isNewLine) {
				// find all selected items and deselect them
				this.productsVMs.forEach((currentProductVM: ProductViewModel) => {
					if (currentProductVM.isSelected && currentProductVM.id !== productVM.id) {
						currentProductVM.isSelected = false;
						this.selectedPackagesIds.delete(currentProductVM.id);
					}
				});
			}

			this.selectedPackagesIds.add(productVM.id);
		}
		else {
			this.selectedPackagesIds.delete(productVM.id);
		}

		// update labels of the selected products
		this.selectedPackagesLabels = this.productsVMs.filter(productVM => productVM.isSelected).map(productVM => productVM.label).join(", ");
	}

	uiTogglePlacement(placementVM: PlacementViewModel): void {
		this.cancelSpaceAvailabilityBatch();

		this.togglePlacement(placementVM);

		if (placementVM.isSelected) {
			++this.selectionStep;
		}

		this.updateProductsValidity();
		this.updateSizesValidity();
		this.updateSpaceAvailability();
		this.notifyPropertyChanged();
	}

	private togglePlacement(placementVM: PlacementViewModel): void {
		// single-select

		if (placementVM.isSelected) {
			// deselect
			placementVM.isSelected = false;
			this._selectedPlacementId = undefined;
			this.selectedPlacementLabel = undefined;
		}
		else {
			if (this._selectedPlacementId) {
				const found = this.placementsVMs.find(pl => pl.id === this._selectedPlacementId);
				if (found) {
					found.isSelected = false;
				}
			}
			placementVM.isSelected = true;
			this._selectedPlacementId = placementVM.id;
			this.selectedPlacementLabel = placementVM.label;
		}
	}

	uiToggleSize(sizeVM: SizeViewModel): void {
		this.cancelSpaceAvailabilityBatch();

		this.toggleSize(sizeVM);

		if (sizeVM.isSelected) {
			++this.selectionStep;
		}

		this.updateProductsValidity();
		this.updatePlacementsValidity();
		this.updateSpaceAvailability();
		this.notifyPropertyChanged();
	}

	private toggleSize(sizeVM: SizeViewModel): void {
		// single-select

		if (sizeVM.isSelected) {
			// deselect
			sizeVM.isSelected = false;
			this.selectedSizeLabel = undefined;
		}
		else {
			// deselect the one currently selected, then select
			if (this.selectedSizeLabel) {
				const found = this.sizesVMs.find(size => size.label === this.selectedSizeLabel);
				if (found) {
					found.isSelected = false;
					//this.selectedSizeLabel = undefined;
				}
			}
			sizeVM.isSelected = true;
			this.selectedSizeLabel = sizeVM.label;
		}
	}

	private updateProductsValidity(): void {
		for (let productVM of this.productsVMs) {
			productVM.isValid = this._productPlacementSizeComboService.isValid(productVM.id, this._selectedPlacementId, this.selectedSizeLabel);
		}
	}

	private updatePlacementsValidity(): void {
		for (let placementVM of this.placementsVMs) {
			// A placement is valid if each "selected product - placement - selected size" combo is valid
			let placementIsValid = true;

			if (this.selectedPackagesIds.size > 0) {
				for (let productId of this.selectedPackagesIds) {
					if (!this._productPlacementSizeComboService.isValid(productId, placementVM.id, this.selectedSizeLabel)) {
						placementIsValid = false;
						break;
					}
				}
			}
			else {
				// no products selected
				placementIsValid = this._productPlacementSizeComboService.isValid(undefined, placementVM.id, this.selectedSizeLabel);
			}

			placementVM.isValid = placementIsValid;
		}

		this.updatePlacementApprovalLegendVisibility();
	}

	private updateSizesValidity(): void {
		//
		for (let sizeVM of this.sizesVMs) {
			// A size is valid if each "selected product - selected placement - size" combo is valid
			let sizeIsValid = true;

			if (this.selectedPackagesIds.size > 0) {
				for (let productId of this.selectedPackagesIds) {
					if (!this._productPlacementSizeComboService.isValid(productId, this._selectedPlacementId, sizeVM.label)) {
						sizeIsValid = false;
						break;
					}
				}
			}
			else {
				// no products selected
				sizeIsValid = this._productPlacementSizeComboService.isValid(undefined, this._selectedPlacementId, sizeVM.label);
			}

			sizeVM.isValid = sizeIsValid;
		}
	}

	private updateMatrixPubs(): void {
		const packagesToRemove = new Set<string>(this.matrixPubsAndIssues.map((val: PubIssuesRecordViewModel) => val.pubId));

		for (const packageId of this.selectedPackagesIds) {
			if (!this.matrixPubsAndIssues.find((val: PubIssuesRecordViewModel) => val.pubId === packageId)) {
				// add the package
				const pckg = this._staticDataCacheService.getPackageById(packageId);
				if (pckg && pckg.label) {
					const newPubIssuesVM = new PubIssuesRecordViewModel(pckg.id, pckg.label);

					const dates = this._dateTimeService.generateDatesStartingFromToday(this.calendarPeriod);
					dates.forEach((d: Date) => {
						const newIssueViewModel = new IssueViewModel(this._dateTimeService.toBackendDate(d), d.getDate());
						newPubIssuesVM.issues.push(newIssueViewModel);
					});

					this.matrixPubsAndIssues.push(newPubIssuesVM)
				}
			}
			else {
				// The packageId is in the this.matrixPubsAndIssues array, and it should stay there
				// Remove its Id from the packagesToRemove
				packagesToRemove.delete(packageId);
			}
		}

		if (packagesToRemove.size > 0) {
			// Remove redundant packages
			packagesToRemove.forEach((packageId: string) => {
				const idx = this.matrixPubsAndIssues.findIndex((val: PubIssuesRecordViewModel) => val.pubId === packageId);
				if (idx > -1) {
					this.matrixPubsAndIssues.splice(idx, 1);
				}
				else {
					this._logger.warning(this.COMPONENT_NAME, `Bug in OrderLineComponent.updateMatrixPubs(): a PubIssuesRecordViewModel is not found in the matrixSelectedPubsAndIssues by packageId=${packageId}`)
				}
			});
		}

		this.updateSelectionCounters();

		this.matrixPubsAndIssues = [...this.matrixPubsAndIssues.sort((a: PubIssuesRecordViewModel, b: PubIssuesRecordViewModel) => a.pubName.localeCompare(b.pubName, this.localeId))];
	}

	private updateSpaceAvailability(): void {
		if (this.selectedPackagesIds.size === 0 || !this._selectedPlacementId || !this.selectedSizeLabel) {
			this.isMatrixVisible = false;
			this.clearSpaceAvailabilityIssues();
			return;
		}

		this.isMatrixVisible = true;

		// during the call-out the user selection might be changed, so have the copies ready when processing the request
		const cachePackagesToProductsMap = new Map<string, Set<string>>();
		const cachePlacementId = this._selectedPlacementId;
		const cacheSizeLabel = this.selectedSizeLabel;

		const dates = this._dateTimeService.generateDatesStartingFromToday(this.calendarPeriod);
		const datesAsString = dates.map(d => this._dateTimeService.toBackendDate(d));

		const spaceAvailabilityRequest = new SpaceAvailabilityRequest();
		spaceAvailabilityRequest.spaces = [];

		// some products are included into a few packages, so just in case user selects a few packages with the same product, let's not include the same product a few times
		const processedProductIds = new Set<string>();

		const selectedPackageIdsSortedByName = this.matrixPubsAndIssues.map((val: PubIssuesRecordViewModel) => val.pubId);

		for (const packageId of selectedPackageIdsSortedByName) {

			const pckg = this._staticDataCacheService.getPackageById(packageId);

			const productIds = new Set<string>();
			cachePackagesToProductsMap.set(packageId, productIds);

			const placement = this._staticDataCacheService.getPlacement(packageId, this._selectedPlacementId);
			if (!placement) {
				this._logger.warning(this.COMPONENT_NAME, `Bug in OrderLineComponent.updateSpaceAvailability(): a placement is not found by packageCode=${packageId} and placementCode=${this._selectedPlacementId}`)
				continue;
			}

			if (pckg && pckg.products && pckg.products.length > 0) {
				for (const product of pckg.products) {

					productIds.add(product.id);

					if (processedProductIds.has(product.id)) {
						continue;
					}
					processedProductIds.add(product.id);

					const placementSize = this._productPlacementSizeComboService.getPlacementSize(packageId, placement.id, this.selectedSizeLabel);
					if (!placementSize) {
						this._logger.warning(this.COMPONENT_NAME, `Bug in OrderLineComponent.updateSpaceAvailability(): a placement Size is not found by packageCode=${packageId}, placementCode=${this._selectedPlacementId}, cacheSizeLabel=${this.selectedSizeLabel}`)
						continue;
					}

					// check each date in datesAsStringp; request data from the backend only if the date is not in the cache
					const missingDates = datesAsString.filter((dateAsString: string) => {
						const key = this.createDateProductPlacementSizeLookupKey(dateAsString, product.id, cachePlacementId, cacheSizeLabel);
						return !this._spaceAvailabilityCache.has(key);
					});

					if (missingDates.length === 0) {
						// all data is in the cache, BE should not be bothered
						continue;
					}

					const spaceAvailabilityRequestRecord = new SpaceAvailabilityRequestRecord();
					spaceAvailabilityRequestRecord.productCode = product.id;
					spaceAvailabilityRequestRecord.placementCode = this._selectedPlacementId;
					spaceAvailabilityRequestRecord.depthMM = placementSize.height || 0;
					//spaceAvailabilityRequestRecord.widthMM = placementSize.calcWidthMM(); // seems obsolete
					spaceAvailabilityRequestRecord.columns = placementSize.columns;
					spaceAvailabilityRequestRecord.moduleCode = placementSize.moduleCode;
					spaceAvailabilityRequestRecord.dates = missingDates; // request only those dates which are missing in the space availability cache

					spaceAvailabilityRequest.spaces.push(spaceAvailabilityRequestRecord);
				}
			}
		}

		if (spaceAvailabilityRequest.spaces.length === 0) {
			// no need to bother the backend, update the Matrix using cache
			this.updateSpaceAvailabilityMatrix(cachePackagesToProductsMap, cachePlacementId, cacheSizeLabel);
			this.spaceAvailabilityRequestProgressValue = 100; // set the progress bar to 100% as all the data is ready
			return;
		}
		else {
			this.updateSpaceAvailabilityMatrix(cachePackagesToProductsMap, cachePlacementId, cacheSizeLabel); // updates whatever possible from the cache first; otherwise just clears the matrix before new data arrives

			const spaceAvailabilityBatch = this.splitSpaceAvailabilityRequest2(spaceAvailabilityRequest);

			let calloutNumber = 0;

			const initialProgessValue = Math.floor(100 / (spaceAvailabilityBatch.length + 1));
			this.spaceAvailabilityRequestProgressValue = initialProgessValue;

			this._spaceAvailabilityCancellationSubject$ = new Subject<void>();

			// the whole batch of space availability requests is handled by one subscriber
			this._spaceAvailabilitySub = this.requestSpaceAvailability(spaceAvailabilityBatch, this._spaceAvailabilityCancellationSubject$).subscribe({
				next: (spaceAvailability: SpaceAvailability) => {
					this.updateSpaceAvailabilityCache(spaceAvailability, cacheSizeLabel);
					this.updateSpaceAvailabilityMatrix(cachePackagesToProductsMap, cachePlacementId, cacheSizeLabel);

					this.spaceAvailabilityRequestProgressValue = Math.round(initialProgessValue + (++calloutNumber / spaceAvailabilityBatch.length) * (100 - initialProgessValue));
				},
				error: (error) => {
					this.spaceAvailabilityRequestProgressValue = 0;
					this._logger.error(this.COMPONENT_NAME, "Space availability request batch failed", error);
					this._alertService.errorByCassError(error);
				}
			});
		}
	}

	private clearSpaceAvailabilityIssues(): void {
		this.matrixPubsAndIssues.forEach((val: PubIssuesRecordViewModel) => {
			val.issues.forEach((issue: IssueViewModel) => {
				if (issue.isSelected && issue.nullableIsValid === true) {
					issue.isSelected = false;
				}
			});
		});

		this.matrixPubsAndIssues = [...this.matrixPubsAndIssues];
	}

	/**
	 * The method sends all the space availability requests one by one, pausing while this.pauseSpaceAvailabilityBatch$ is true.
	 * The idea is to let the Calc request and response to be processed first.
	 * @param requests Array<SpaceAvailabilityRequest>
	 * @param cancellationSubject$ is used to cancel the execution of the active batch
	 * @returns Observable<SpaceAvailability> receiving separate SpaceAvailability response for each request
	 */
	requestSpaceAvailability(requests: SpaceAvailabilityRequest[], cancellationSubject$: Subject<void>): Observable<SpaceAvailability> {

		return new Observable<SpaceAvailability>(observer => {
			let index = 0;

			const processRequest = () => {
				if (index < requests.length) {
					const request = requests[index];

					const sub = this.pauseSpaceAvailabilityBatch$
						.pipe(
							switchMap(isPaused => {
								if (isPaused) {
									return this.pauseSpaceAvailabilityBatch$.pipe(filter(p => !p), take(1));
								}
								else {
									return of(false);
								}
							}),
							switchMap(() => {
								// send one request
								return this._orderService.getSpaceAvailability(request).pipe(
									takeUntil(cancellationSubject$)
								);
							}),
							take(1),
							takeUntil(cancellationSubject$)
						)
						.subscribe({
							next: (response) => {
								observer.next(response);
								++index;
								processRequest();
							},
							error: (error) => {
								observer.error(error);
							}
						});

					this._subs.add(sub);
				}
				else {
					observer.complete(); // all requests are sent
				}
			};

			processRequest();
		});
	}

	private splitSpaceAvailabilityRequest2(spaceAvailabilityRequest: SpaceAvailabilityRequest): SpaceAvailabilityRequest[] {
		if (!spaceAvailabilityRequest || !spaceAvailabilityRequest.spaces || spaceAvailabilityRequest.spaces.length === 0) {
			return [spaceAvailabilityRequest]; // not happening, just shushes up the code analyzer
		}

		const result: SpaceAvailabilityRequest[] = [];

		const chunks: { startDate: string, record: SpaceAvailabilityRequestRecord }[] = [];

		for (const rec of spaceAvailabilityRequest.spaces) {
			if (rec.dates && rec.dates.length > 0) {
				if (rec.dates.length > this.SPACE_AVAILABLITY__MAX_DATES_PER_PRODUCT) {
					// the array of dates is too long and this request needs to be split
					const numberOfSplits = Math.ceil(rec.dates.length / this.SPACE_AVAILABLITY__MAX_DATES_PER_PRODUCT);

					for (let i = 0; i < numberOfSplits; i++) {
						const start = i * this.SPACE_AVAILABLITY__MAX_DATES_PER_PRODUCT;
						const end = start + this.SPACE_AVAILABLITY__MAX_DATES_PER_PRODUCT;
						const chunkDates = rec.dates.slice(start, end);

						const startDate: string = chunkDates[0];

						const newRec: SpaceAvailabilityRequestRecord = {
							dates: chunkDates,
							productCode: rec.productCode,
							placementCode: rec.placementCode,
							columns: rec.columns,
							depthMM: rec.depthMM,
							moduleCode: rec.moduleCode
						};

						chunks.push({ startDate: startDate, record: newRec })
					}
				}
				else {
					// the array of dates does not need to be split as its length is less than the limit
					chunks.push({ startDate: rec.dates[0], record: rec })
				}
			}
		}

		// sort the chunks by the start date of the record
		chunks.sort((a, b) => a.startDate.localeCompare(b.startDate));

		const copiedProductCodes = new Set<string | undefined>(); // undefined is used here just to supress code analyzer errors
		let currentSpaceAvailabilityRequest: SpaceAvailabilityRequest | undefined = undefined;

		// put chunks into requests, no more than max products per request
		for (const chunk of chunks) {
			if (!currentSpaceAvailabilityRequest || copiedProductCodes.size === this.SPACE_AVAILABLITY__MAX_PRODUCTS_PER_REQUEST || copiedProductCodes.has(chunk.record.productCode)) {
				currentSpaceAvailabilityRequest = new SpaceAvailabilityRequest();
				currentSpaceAvailabilityRequest.spaces = [];
				result.push(currentSpaceAvailabilityRequest);
				copiedProductCodes.clear();
			}

			currentSpaceAvailabilityRequest.spaces?.push(chunk.record);
			copiedProductCodes.add(chunk.record.productCode);
		}

		return result;
	}

	private updateSpaceAvailabilityCache(spaceAvailability: SpaceAvailability, sizeLabel: string): void {
		if (!spaceAvailability.spaces || spaceAvailability.spaces.length === 0) {
			this._logger.error(this.COMPONENT_NAME, "The spaces availability response is empty");
			return;
		}

		// Add the space availability data into the local cache: per date, product, placement, and size
		for (const spaceAvailabilityRecord of spaceAvailability.spaces) {
			if (spaceAvailabilityRecord.date && spaceAvailabilityRecord.productCode && spaceAvailabilityRecord.placementCode && spaceAvailabilityRecord.available !== undefined) {
				const key = this.createDateProductPlacementSizeLookupKey(spaceAvailabilityRecord.date, spaceAvailabilityRecord.productCode, spaceAvailabilityRecord.placementCode, sizeLabel);
				this._spaceAvailabilityCache.set(key, spaceAvailabilityRecord.available);
			}
		}
	}

	private updateSpaceAvailabilityMatrix(packagesToProductsMap: Map<string, Set<string>>, placementId: string, sizeLabel: string): void {
		for (const [key, value] of packagesToProductsMap) {
			// key: string, package Id
			// value: Set<string>, products Ids in this package
			const packageId: string = key;
			const productIds: Set<string> = value;

			const pubIssuesVM = this.matrixPubsAndIssues.find((val: PubIssuesRecordViewModel) => val.pubId === packageId);
			if (pubIssuesVM) {
				pubIssuesVM.issues.forEach((issue: IssueViewModel) => issue.nullableIsValid = this.getDateAvailabilityResultForProductsSetPlacementSize(issue.dateCode, packageId, productIds, placementId, sizeLabel));
			}
		}
	}

	/**
	 * The method checks the availability cache for each combination of product-placement-size-date.
	 * If each combo is valid, the result is true. If any cache value is invalid, the result is fasle. If any cache value is not present, the result is undefined.
	 * @param dateString
	 * @param packageId
	 * @param productIds set of products in the package
	 * @param placementId
	 * @param sizeLabel
	 * @returns true if each combo is valid, false if any combo is invalid, undefined if any combo is not in the cache
	 */
	private getDateAvailabilityResultForProductsSetPlacementSize(dateString: string, packageId: string, productIds: Set<string>, placementId: string, sizeLabel: string): boolean | undefined {
		// Note: the packageId is needed only to report a bug in case the product ids set is empty
		if (productIds.size === 0) {
			this._logger.error(this.COMPONENT_NAME, `A bug in the compoennt. The getDateAvailabilityResultForProductsSetPlacementSize() method: the productIds set is empty. PackageId="${packageId}", dateString="${dateString}", placementId="${placementId}", sizeLabel="${sizeLabel}".`);
			return false;
		}

		for (const productId of productIds) {
			const nullableCacheRes = this.getDateAvailabilityCacheForProductPlacementSize(dateString, productId, placementId, sizeLabel);

			if (nullableCacheRes === false) {
				return false;
			}
			else if (nullableCacheRes === undefined) {
				return undefined;
			}
		}

		return true;
	}

	/**
	 * The method checks the availability cache for given combination of product-placement-size-date
	 * @param dateString
	 * @param productId
	 * @param placementId
	 * @param sizeLabel
	 * @returns true if the combination is valid, false is compibation is not valid, undefined if the combination is not cached
	 */
	private getDateAvailabilityCacheForProductPlacementSize(dateString: string, productId: string, placementId: string, sizeLabel: string): boolean | undefined {
		const lookupKey = this.createDateProductPlacementSizeLookupKey(dateString, productId, placementId, sizeLabel);
		return this._spaceAvailabilityCache.get(lookupKey);
	}

	private cancelSpaceAvailabilityBatch(): void {
		this._spaceAvailabilityCancellationSubject$?.next();
		this._spaceAvailabilityCancellationSubject$?.complete();
		this._spaceAvailabilitySub?.unsubscribe();

		// reset the progress bar
		this.spaceAvailabilityRequestProgressValue = 0;
	}

	private createDateProductPlacementSizeLookupKey(dateString: string, productCode: string, placementCode: string, sizeLabel: string): string {
		return `${dateString}-${productCode}-${placementCode}-${sizeLabel}`;
	}

	onProductMouseEnter(productId: string, event: MouseEvent): void {
		if (event && event.target && productId) {
			this._popoverService.notifyMouseEntersPackageLabel(productId, event.target);
		}
	}

	onProductMouseLeave(productId: string, event: MouseEvent): void {
		if (productId) {
			this._popoverService.notifyMouseLeavesPackageLabel(productId);
		}
	}

	// Custom filters configured on the products and shown as toggles above the products
	productFilters = new Map<string, ProductFilter>();

	// This is used to override the KeyMap pipe's default ordering by key.
	productFilterSortFn(a: KeyValue<string, ProductFilter>, b: KeyValue<string, ProductFilter>) {
		if (a.value.categorySortNumber !== b.value.categorySortNumber) {
			return a.value.categorySortNumber - b.value.categorySortNumber;
		}
		return a.value.categoryLabel.localeCompare(b.value.categoryLabel, this.localeId);
	}

	// Apply the filters to products
	showFilteredProduct(productVM: ProductViewModel) {
		if (this.filterProductName && !productVM.label.toLowerCase().includes(this.filterProductName.toLowerCase())) {
			return false;
		}
		if (this.productFilters.size > 0) {
			// Note: the UI productVM is actually a package containing one or more products
			// We show the package if it contains at least one product matching all of the selected filters
			const pkg = this._staticDataCacheService.getPackageById(productVM.id);
			for (let filter of this.productFilters) {
				if (filter[1].selected) {
					if (!pkg?.products?.some(prod => prod.productFilters.some(f => f.categoryLabel === filter[0] && f.valueLabel == filter[1].selected))) {
						return false;
					}
				}
			}
		}
		return true;
	}

	// Product name filter string
	filterProductName: string = "";

	// Add all the valid products that are currently shown by the filtering
	uiSelectFilteredProducts() {
		var selectionChanged = false;
		for (let productVM of this.productsVMs) {
			if (!productVM.isSelected && productVM.isValid && this.showFilteredProduct(productVM)) {
				this.toggleProduct(productVM);
				selectionChanged = true;
			}
		}

		if (selectionChanged) {
			this.cancelSpaceAvailabilityBatch();

			this.updatePlacementsValidity();
			this.updateSizesValidity();
			this.updateMatrixPubs();
			this.updateSpaceAvailability();
			this.notifyPropertyChanged();
		}
	}

	// Clear all products completely
	uiClearAllProducts() {
		var selectionChanged = false;
		for (let productVM of this.productsVMs) {
			if (productVM.isSelected) {
				this.toggleProduct(productVM);
				selectionChanged = true;
			}
		}

		if (selectionChanged) {
			this.cancelSpaceAvailabilityBatch();

			this.updatePlacementsValidity();
			this.updateSizesValidity();
			this.updateMatrixPubs();

			this.notifyPropertyChanged();
		}
	}

	resetProductsFilters(): void {
		this.productFilters.forEach((productFilter: ProductFilter) => productFilter.selected = "");
		this.filterProductName = "";
	}

	private updateSelectionCounters(): void {
		let insertionCounter = 0;
		let uniqueDates = new Set<string>();

		this.matrixPubsAndIssues.forEach((pubIssues: PubIssuesRecordViewModel) => {
			let pubInsertionCounter = 0;

			pubIssues.issues.forEach((issue: IssueViewModel) => {
				if (issue.isSelected) {
					++pubInsertionCounter;
					uniqueDates.add(issue.dateCode);
				}
			});

			pubIssues.numberOfSelectedDates = pubInsertionCounter;

			insertionCounter += pubInsertionCounter;
		});

		this.insertionCounter = insertionCounter;
		this.dateCounter = uniqueDates.size;
	}

	async okAction(): Promise<void> {

		if (this.updatedOrder && this.originalOrder) {

			if (!await this.warnUserOfStrictPackageGroup(this.originalOrder, this.updatedOrder)) {
				return;
			}

			if (this.originalOrderAdDetail && this.updatedOrderAdDetail) {
				// Edit Existing Order Line mode is active; replace the original order line with the updated one
				this.updatedOrder.orderAdDetails.forEach((orderLine: OrderAdDetail, index) => {
					if (orderLine === this.originalOrderAdDetail) {
						// replace the order line with the updated one
						if (this.updatedOrder && this.updatedOrderAdDetail) {
							this.updatedOrder.orderAdDetails[index] = this.updatedOrderAdDetail;
						}
					}
				});
			}

			// send updated order
			const state = new OrderNavigationState();
			state.order = this.updatedOrder;
			state.orderOwner = this._orderOwner;
			state.needsCalc = false;
			state.isUpdated = true;
			state.orderLinesTableSort = this._inputState?.orderLinesTableSort;

			this._router.navigate([AppPath.Order], { state: state });
		}
		else {
			this._logger.error(this.COMPONENT_NAME, "okAction(): the updated order is empty but the user has managed to click Apply. Possible bug.");
		}
	}

	cancelAction(): void {
		// send back the original unchanged order
		const state = new OrderNavigationState();
		state.order = this.originalOrder;
		state.orderOwner = this._orderOwner;
		state.isUpdated = this._orderHasUnsavedChanges; // order can have changes made before this Order Line 'session'
		state.orderLinesTableSort = this._inputState?.orderLinesTableSort;

		this._router.navigate([AppPath.Order], { state: state });
	}

	clearAllDates(): void {
		this.matrixPubsAndIssues.forEach((pubIssues: PubIssuesRecordViewModel) => {
			pubIssues.issues.forEach((issue: IssueViewModel) => {
				if (issue.isSelected) {
					issue.isSelected = false;
				}
			});
			pubIssues.numberOfSelectedDates = 0;
		});

		this.matrixPubsAndIssues = [...this.matrixPubsAndIssues];
		this.insertionCounter = 0;
		this.dateCounter = 0;
	}

	private canCalculate(): boolean {
		return this.selectedPackagesIds.size > 0
			&& !!this._selectedPlacementId
			&& !!this.selectedSizeLabel
			&& this.matrixPubsAndIssues.every((pubIssues: PubIssuesRecordViewModel) => {
				return pubIssues.issues.some((issue: IssueViewModel) => issue.isSelected);
			});
	}

	private notifyPropertyChanged(): void {
		// set the timestamp
		this._lastChangeTimeStamp = Date.now();

		// disable Apply button
		this.canApply = false;

		if (!this.canCalculate()) {
			return;
		}

		// all the params are entered, reset the price and trigger debounced Calc
		this.resetCosting();
		this._calc$.next(this._lastChangeTimeStamp);
	}

	/**
	 * Creates new order lines based on user selection
	 * @returns
	 */
	private createOrderLines(): OrderAdDetail[] {
		const newOrderLines: OrderAdDetail[] = [];

		this.matrixPubsAndIssues.forEach((pubIssues: PubIssuesRecordViewModel) => {
			pubIssues.issues.forEach((issue: IssueViewModel) => {
				if (issue.isSelected) {
					const adDetail = new OrderAdDetail();
					this.updateOrderLine(adDetail, pubIssues.pubId, issue.dateCode);
					newOrderLines.push(adDetail);
				}
			});
		});

		return newOrderLines;
	}

	/**
	 * Updates existing order line based on the user-entered information
	 * @param adDetail
	 * @param packageCode
	 * @param dateString
	 */
	private updateOrderLine(adDetail: OrderAdDetail, packageCode: string, dateString: string): void {
		// updates existing single order line based on the user input

		if (this.restriction.canChangeProduct) {
			adDetail.packageId = packageCode;

			const staticDataPackage = this._staticDataCacheService.getPackageById(adDetail.packageId);
			if (staticDataPackage && staticDataPackage.products && staticDataPackage.products.length > 0) {
				// Note: the code uses ONLY ONE product per package. But anyway we currently don't use this field when sending the request to the BE
				adDetail.productId = staticDataPackage.products[0].id;
			}
			else {
				this._logger.error(this.COMPONENT_NAME, `Cannot retrieve the package static data by packageId="${adDetail.packageId}". Possible bug.`);
			}
		}

		if (this.restriction.canChangePlacement) {
			adDetail.placementId = this._selectedPlacementId || "";
		}

		if (this.restriction.canChangeSize) {
			adDetail.size = this.selectedSizeLabel || "";

			const placementSizeStaticData = this._productPlacementSizeComboService.getPlacementSize(adDetail.packageId, adDetail.placementId, adDetail.size);
			if (placementSizeStaticData) {
				adDetail.columns = placementSizeStaticData.columns || 0;
				adDetail.depth = placementSizeStaticData.height || 0;
				// adDetail.width = placementSize.calcWidthMM() || 0;

				// Clear the materialId so that new material will be generated for changed size
				adDetail.materialId = ''
			}
			else {
				this._logger.error(this.COMPONENT_NAME, `Cannot retrieve the placement size static data by its label="${this.selectedSizeLabel}". Possible bug.`);
			}

			adDetail.moduleCode = "";
			const sizeStaticData = this._productPlacementSizeComboService.getSize(adDetail.packageId, adDetail.placementId, adDetail.size);
			if (sizeStaticData) {
				if (sizeStaticData.moduleCode) {
					adDetail.moduleCode = sizeStaticData.moduleCode;
				}
			}
			else {
				this._logger.error(this.COMPONENT_NAME, `Cannot retrieve the size static data by PackageCode="${adDetail.packageId}", PlacementCode="${adDetail.placementId}", Label="${adDetail.size}". Possible bug.`);
			}
		}

		if (this.restriction.canChangeIssueDate) {
			adDetail.issueDate = dateString;
		}

		if (this.restriction.canChangeOtherFields) {
			adDetail.placementRequest = this.placementRequest.value || undefined;
		}

		const placementSizeRelation = this._productPlacementSizeComboService.getPlacementSizeRelation(adDetail.packageId, adDetail.placementId);
		if (placementSizeRelation) {
			if (placementSizeRelation.requiresApproval) {
				if (!this._statusService.doesAdDetailHaveStatus(adDetail, OrderAdDetailStatus.ApprovalPending)) {
					// add the ApprovalPending status
					adDetail.statusFlags.push(OrderAdDetailStatus.ApprovalPending);
				}
			}
			else {
				if (this._statusService.doesAdDetailHaveStatus(adDetail, OrderAdDetailStatus.ApprovalPending)) {
					// remove the ApprovalPending status
					const approvalPendingStatusUppercase = OrderAdDetailStatus.ApprovalPending.toUpperCase();
					adDetail.statusFlags = adDetail.statusFlags.filter((status: string) => status.toUpperCase() !== approvalPendingStatusUppercase);
				}
			}
		}
		else {
			this._logger.error(this.COMPONENT_NAME, `Cannot retrieve the placement-size relation by PackageCode="${adDetail.packageId}" and PlacementCode="${adDetail.placementId}". Possible bug.`);
		}
	}

	/**
	 * Based on the user-entered information, updates the given order: creates new order lines or updates existing order line.
	 * @param order
	 * @returns new order lines or updated existing order line. Undefined usually means a bug in the code.
	 */
	private updateOrderFromForm(order: Order): OrderAdDetail[] | OrderAdDetail | undefined {
		if (!this._selectedPlacementId || !this.selectedSizeLabel) {
			return undefined;
		}

		let newOrderLines: OrderAdDetail[] | undefined = undefined;
		if (this.isNewLine) {
			// Attention. In case user adds new issue date after Calculate was performed, new order lines must be added to initial order lines and not to existintg order lines to avoid adding some of them a few times
			newOrderLines = this.createOrderLines();
			order.orderAdDetails = [...this._initialOrderLines, ...newOrderLines];
		}
		else if (this.updatedOrderAdDetail) {
			// Existing order line must not be added to the order as it is already a part of the order

			if (this.matrixPubsAndIssues.length > 0) {
				const pubAndIssues = this.matrixPubsAndIssues[0];
				const selectedIssue = pubAndIssues.issues.find((issue: IssueViewModel) => issue.isSelected);
				if (selectedIssue) {
					this.updateOrderLine(this.updatedOrderAdDetail, pubAndIssues.pubId, selectedIssue.dateCode);

					if (this.updatedOrderAdDetail.materialId) {
						// CASS-421: check that this material is not used by another order line with a different size (depth and width)
						if (!this.checkSizeComplianceForMaterialId(order.orderAdDetails, this.updatedOrderAdDetail, this.updatedOrderAdDetail.materialId)) {
							this.updatedOrderAdDetail.materialId = "";
						}
					}
				}
			}
			else {
				this._logger.error(this.COMPONENT_NAME, "Bug: the component is in the EDIT mode, but matrixPubsAndIssues array is empty.");
			}
		}

		this._orderHelperService.updateOrderStartEndDates(order);

		// TODO: Remove this after checking all the cases it has been moved to deferredCalc() method
		// Removed because it will only generate material for new line but not for the updated line
		// this._orderHelperService.updateMaterial(order.orderAdDetails);

		if (this.isNewLine) {
			return newOrderLines;
		}
		else if (this.updatedOrderAdDetail) {
			return this.updatedOrderAdDetail;
		}
		else {
			return undefined;
		}
	}

	/**
	 * Creates order lines based on the current user input, calculates the order, and updates the costing information.
	 * It saves the updated order in the this.updatedOrder property so it can be used as a calculated order without further re-calculation.
	 * In case the given timestamp differs from this.lastChangeTimeStamp when the Calc result is received, the method does nothing.
	 * This means the data has changed by the user and the method's current data is not relevant anymore.
	 * @param timeStamp
	 * @returns
	 */
	private deferredCalc(timeStamp: number): void {
		// check the given timestamp with the timestamp of the last change and exit if differs
		if (timeStamp !== this._lastChangeTimeStamp || !this.originalOrder) {
			return;
		}

		// Copy the original order and update the copy; use initial order lines.
		// This way original order remains intact and can be sent back in case the user cancels the operation.
		const orderCopy: Order = Object.assign({}, this.originalOrder);
		orderCopy.orderAdDetails = [...this._initialOrderLines];

		const newOrUpdatedOrderLines = this.updateOrderFromForm(orderCopy);

		if (this.originalOrderAdDetail && newOrUpdatedOrderLines && !Array.isArray(newOrUpdatedOrderLines)) {
			// in Edit Order Line mode, replace the original order line in the order with the updated one
			const idx = this._initialOrderLines.findIndex((orderAdDetail: OrderAdDetail) => orderAdDetail === this.originalOrderAdDetail);
			if (idx > -1) {
				orderCopy.orderAdDetails[idx] = newOrUpdatedOrderLines as OrderAdDetail;
			}
		}

		const calcRequest = this._calculatePriceRequestService.createCalculateDisplayOrderRequest(orderCopy);

		this._callingOut$.next(true);
		this.pauseSpaceAvailabilityBatch$.next(true);
		this._orderService.calculatePrice(calcRequest).subscribe({
			next: (calculatedOrder: Order) => {
				this._callingOut$.next(false);
				this.pauseSpaceAvailabilityBatch$.next(false);

				// The order properies could be changed during the Calc callout as the result of user interaction; don't process obsolete response.
				if (timeStamp !== this._lastChangeTimeStamp) {
					return;
				}

				const orderLinesArray = this.getOrderLinesAsArray(newOrUpdatedOrderLines);
				if (!orderLinesArray) {
					this._logger.error(this.COMPONENT_NAME, "deferredCalc(): the order lines parameter is empty. Possible bug in the FE or BE.");
					return;
				}

				// CASS-353: all order lines can be affected by the recent change. Update pricing for all order lines.
				this._orderHelperService.updateOrderLinesWithCalcResult(orderCopy.orderAdDetails, calculatedOrder.orderAdDetails);

				// CASS-421: try to restore original material id if the other order lines do not have different depth and width for this material id
				if (this.originalOrderAdDetail && this.originalOrderAdDetail.materialId && this.updatedOrderAdDetail) {
					if (this.checkSizeComplianceForMaterialId(orderCopy.orderAdDetails, this.updatedOrderAdDetail, this.originalOrderAdDetail.materialId)) {
						this.updatedOrderAdDetail.materialId = this.originalOrderAdDetail.materialId;
					}
				}

				this._orderHelperService.updateOrderCostingWithCalcResult(orderCopy, calculatedOrder);

				// update component costing only using just added or edited order line(s)
				this.updateComponentCosting(orderLinesArray);

				// don't update material ids as the Calc response does not provide correct data
				// It will generate material for updated or new order lines if needed
				// this._orderHelperService.updateMaterial(orderCopy.orderAdDetails)
				// this._orderHelperService.changeMaterial(orderCopy.orderAdDetails, []);

				this.updatedOrder = orderCopy;
				this.canApply = true;
			},
			error: (error: CassError) => {
				this._callingOut$.next(false);
				this.pauseSpaceAvailabilityBatch$.next(false);

				this._logger.error(this.COMPONENT_NAME, "Calculated failed", error);
				this._alertService.errorByCassError(error);
			}
		});
	}

	private resetCosting(): void {
		this.priceGross = 0;
		this.priceNet = 0;
	}

	getOrderLinesAsArray(orderLines: OrderAdDetail[] | OrderAdDetail | undefined): OrderAdDetail[] | undefined {
		if (!orderLines) {
			return undefined;
		}

		let orderLinesArray: OrderAdDetail[] | undefined;

		if (this.isNewLine && Array.isArray(orderLines)) {
			orderLinesArray = orderLines;
		}
		else if (orderLines) {
			orderLinesArray = [orderLines as OrderAdDetail];
		}
		else {
			this._logger.warning(this.COMPONENT_NAME, "getOrderLinesAsArray(): the orderLines parameter is empty. Possible bug.");
			return undefined;
		}

		return orderLinesArray;
	}

	/**
	 * Updates the UI costing information displayed to the user, based on the provided order lines.
	 * The method is expected to be called when creating new order lines, and when editing single existing order line.
	 * @param orderLines
	 */
	private updateComponentCosting(orderLines: OrderAdDetail[]): void {
		let runtimePriceGross = 0;
		let runtimeDiscountAmount = 0;
		let runtimeCommissionAmount = 0;

		orderLines.forEach((newOrUpdatedOrderLine: OrderAdDetail) => {
			runtimePriceGross += newOrUpdatedOrderLine.priceGross;
			runtimeDiscountAmount += newOrUpdatedOrderLine.discountAmount;
			runtimeCommissionAmount += newOrUpdatedOrderLine.commissionAmount;
		});

		this.priceGross = runtimePriceGross;
		this.discount = runtimeDiscountAmount;
		this.priceNetExComm = runtimePriceGross - runtimeDiscountAmount;
		this.commission = runtimeCommissionAmount;
		this.priceNet = this.priceNetExComm - runtimeCommissionAmount;
	}

	/**
	 * The Placement Approval Legend must be shown only when any of the valid (and shown) placements have the requiresApproval flag set. CASS-168
	 */
	private updatePlacementApprovalLegendVisibility(): void {
		this.showPlacementApprovalLegend = this.placementsVMs.some((placementVM: PlacementViewModel) => placementVM.isValid && placementVM.requiresApproval);
	}

	selectionPanelExpanded(step: number): void {
		this.selectionStep = step;
	}

	/**
	 * Called from toggleProduct - i.e. the UI event when selecting or deselecting a package.
	 * If a package contained in a strict package group is selected or deselected, the other package in the group must also be selected/deselected.
	 * Only applies if not editing an existing order line.
	 * @param productVM The package being toggled
	 */
	toggleStrictPackageGroup(productVM: ProductViewModel) {
		if (!this.isNewLine) return;
		const strictPackageGroups = this._appConfigService.config.strictPackageGroups;
		const group = strictPackageGroups.find(x => x.includes(productVM.id));
		if (group) {
			for (let packId of group) {
				if (packId !== productVM.id) {
					const otherProductVM = this.productsVMs.find(x => x.id === packId);
					if (otherProductVM && otherProductVM.isSelected !== productVM.isSelected) {
						this.toggleProduct(otherProductVM);
					}
				}
			}
		}
	}

	/**
	 * Check for compliance with strict package groups rule and warn user if they are breaking it.
	 * Only warn if the rule is not already broken (because the user will be trying to fix it)
	 * @param originalOrder The order before changes to lines.
	 * @param updatedOrder The order after changes to lines.
	 * @returns Promise<False> if rule fails *and* user wants to abort.
	 */
	async warnUserOfStrictPackageGroup(originalOrder: Order, newOrder: Order): Promise<boolean> {
		//
		if (!this._strictPackageGroupsService.hasInvalidStrictPackageGroups(originalOrder)) {
			const msg = this._strictPackageGroupsService.hasInvalidStrictPackageGroups(newOrder);
			if (msg) {
				const continueActionId = "continue";
				const cancelActionId = "cancel";
				const dialogData = new DialogData();
				dialogData.titleKey = "ORDER_BASKET.VALIDATION_MESSAGES.ARE_YOU_SURE_DIALOG_TITLE";
				dialogData.messageKey = "ORDER_BASKET.VALIDATION_MESSAGES.STRICT_PACKAGE_GROUPS_NOT_VALID";
				dialogData.messageParams = { data: msg };
				dialogData.actions = [
					new DialogAction(continueActionId, "ACTION_BUTTON_LABELS.CONTINUE", true),
					new DialogAction(cancelActionId, "ACTION_BUTTON_LABELS.CANCEL", false),
				];
				return firstValueFrom(this._dialog
					.open(DialogComponent, { data: dialogData })
					.afterClosed()
					.pipe(map(actionId => actionId === continueActionId)));
			}
		}
		return true; // ok to continue
	}

	private sortProductsVMs(productsVMs: ProductViewModel[]): ProductViewModel[] {
		const vms = [...productsVMs]; // array.sort() sorts the items in place so Angular does not re-render the array
		return vms.sort((a: ProductViewModel, b: ProductViewModel) => a.label.localeCompare(b.label, this.localeId));
	}

	private sortPlacementsVMs(placementsVMs: PlacementViewModel[]): PlacementViewModel[] {
		const vms = [...placementsVMs];
		return vms.sort((a: PlacementViewModel, b: PlacementViewModel) => a.label.localeCompare(b.label, this.localeId));
	}

	private createProductFilters(products: Product[]): Map<string, ProductFilter> {

		const productFilters = new Map<string, ProductFilter>();

		if (products && products.length > 0) {
			for (const product of products) {
				for (const filter of product.productFilters) {
					let productFilter: ProductFilter | undefined = productFilters.get(filter.categoryLabel);

					if (!productFilter) {
						productFilter = {
							selected: '',
							options: [],
							searchQuery: '',
							controlType: filter.categoryType,
							categorySortNumber: filter.categorySortNumber || 0, // fallback to 0
							categoryLabel: filter.categoryLabel
						} as ProductFilter;
						productFilters.set(filter.categoryLabel, productFilter);
					}

					if (!productFilter.options.find((option: KeyValue<number, string>) => option.key === filter.valueSortNumber && option.value === filter.valueLabel)) {
						const option: KeyValue<number, string> = { key: filter.valueSortNumber || 0, value: filter.valueLabel };
						productFilter.options.push(option);
					}
				}
			}
		}

		return productFilters;
	}

	/**
	 * For the given material id, checks that other order lines do not have the depth and width different from the master order line
	 * @param allOrderLines all order lines in the order
	 * @param masterOrderLine order line which provides width and depth for the given material id
	 * @param materialId the method examines width and depth of the order lines with this material id only; order lines with different material ids are ignored
	 * @returns true if all the order lines with this material id contain the same depth and width
	 */
	private checkSizeComplianceForMaterialId(allOrderLines: OrderAdDetail[], masterOrderLine: OrderAdDetail, materialId: string): boolean {
		if (materialId) {
			const isMaterialInvalid = allOrderLines.some((orderLine: OrderAdDetail) => {
				if (orderLine === masterOrderLine) {
					// ignore self by reference
					return false;
				}

				if (orderLine.id && orderLine.id === masterOrderLine.id) {
					// ignore self by id in scenarios where the copy of original order line is provided as master
					return false;
				}

				if (orderLine.materialId === materialId && (orderLine.depth !== masterOrderLine.depth || orderLine.width !== masterOrderLine.width)) {
					return true;
				}

				return false;
			});

			return !isMaterialInvalid;
		}

		return true;
	}
}

